/**
 * Three.js archetype library for SKORPIO tables + BELINDA chairs.
 *
 * Every visible top shape in the SKORPIO range maps to one builder here:
 *   buildBase()                — shared X-shape sculptural base
 *   buildFlatRectTop()         — CL/CA/KR plain rectangles
 *   buildSagTop()              — shaped (rounded short ends) — sag.* sizes
 *   buildBiscuitTop()          — biscuit.220x120 only (heavily rounded corners)
 *   buildPolygonTop()          — polygon.300x130 only (hexagonal)
 *   buildRoundTop()            — RND glass, KRD ceramic, KPR ceramic
 *   buildKpTop()               — Keramik Premium thick top + brushed rim
 *   buildKwTop()               — wood ring + ceramic insert disc (Ker-Wood)
 *   buildWoodSTop()            — irregular live-edge solid wood (S-variant)
 *   buildWoodBTop()            — barrel-edged wood (B-variant)
 *   buildWoodMTop()            — Masterwood boat-shape (M-variant)
 *   buildWoodYTop()            — bevelled dual-tone wood (Y-variant)
 *
 *   buildTableMesh(item)       — composites base + dispatched top
 *   buildChairMesh(item)       — proper shell + splayed legs + (B) armrests
 *
 * All dimensions enter as cm from the data layer; we convert to mm internally
 * (×10) so the WebGL units feel right next to the methodology's bathroom
 * configurator. Origin = floor centre; Y is up.
 */

import * as THREE from 'three';
import { TDSLoader } from 'three/examples/jsm/loaders/TDSLoader.js';
import {
  SIZES_BY_COLLECTION, BASE_BY_CODE,
  CHAIR_SIZES,
  CHAIR_FABRICS, CHAIR_MICRO_NUBUCK, CHAIR_SYNTHETIC_LEATHER,
  CHAIR_SOFT_LEATHER, CHAIR_SOFT_LEATHER_GLOVE,
  photoTextureUrl,
} from '../data/catalog.js';

// ═══════════════════════════════════════════════════════════════════════════
//  REAL-MODEL LOADING — Cattelan-provided .3ds files
//
//  /public/models/BELINDA.3ds          (chair, 1.4MB)
//  /public/models/SKORPIO_240x120.3ds  (table at the 240×120 reference size)
//
//  We load each model lazily, cache the normalised THREE.Group, and clone +
//  re-material per item. The model arrives async so the build functions
//  return an empty Group up front and add the loaded geometry when ready.
// ═══════════════════════════════════════════════════════════════════════════

const __modelCache = new Map();

function loadTDS(url) {
  if (__modelCache.has(url)) return __modelCache.get(url);
  const promise = new Promise((resolve, reject) => {
    new TDSLoader().load(url, (raw) => {
      // .3ds files are Z-up — bake -PI/2 X rotation into each mesh's geometry
      // so per-vertex Y values equal world Y. This makes geometry filtering
      // (split-by-plane for table top removal) straightforward.
      raw.rotation.x = -Math.PI / 2;
      raw.updateMatrixWorld(true);
      const visited = new Set();
      raw.traverse(o => {
        if (o.isMesh && o.geometry && !visited.has(o.geometry)) {
          o.geometry.applyMatrix4(o.matrixWorld);
          visited.add(o.geometry);
        }
        if (o.isMesh) {
          o.position.set(0, 0, 0);
          o.rotation.set(0, 0, 0);
          o.scale.set(1, 1, 1);
          o.updateMatrix();
        }
      });
      raw.rotation.set(0, 0, 0);
      raw.position.set(0, 0, 0);
      raw.updateMatrixWorld(true);

      // Translate geometries so the model is centred on XZ and sits on y=0.
      const box = new THREE.Box3().setFromObject(raw);
      const ctr = new THREE.Vector3(); box.getCenter(ctr);
      const offsetX = -ctr.x, offsetY = -box.min.y, offsetZ = -ctr.z;
      const visited2 = new Set();
      raw.traverse(o => {
        if (o.isMesh && o.geometry && !visited2.has(o.geometry)) {
          o.geometry.translate(offsetX, offsetY, offsetZ);
          visited2.add(o.geometry);
        }
      });
      raw.updateMatrixWorld(true);

      // Final extents + per-mesh classification stash.
      const finalBox = new THREE.Box3().setFromObject(raw);
      const finalSize = new THREE.Vector3(); finalBox.getSize(finalSize);
      const minY = finalBox.min.y, totalH = Math.max(0.001, finalSize.y);
      raw.traverse(o => {
        if (!o.isMesh) return;
        const mb = new THREE.Box3().setFromObject(o);
        const cy = (mb.min.y + mb.max.y) / 2;
        const dy = mb.max.y - mb.min.y;
        o.userData.cy_norm = (cy - minY) / totalH;
        o.userData.dy_norm = dy / totalH;
        o.castShadow = true;
        o.receiveShadow = true;
      });

      resolve({ obj: raw, size: finalSize });
    }, undefined, reject);
  });
  __modelCache.set(url, promise);
  return promise;
}

/**
 * Return a new BufferGeometry containing only the triangles of `geom` whose
 * centroid Y is strictly below `thresholdY`. Used to strip the top portion
 * of a monolithic table .3ds so we can overlay a parametric top with the
 * correct material without z-fighting.
 */
function geometryRemoveTopRegion(geom, thresholdY) {
  const pos = geom.attributes.position;
  if (!pos) return geom;
  const idx = geom.index;
  const keep = [];
  if (idx) {
    const triCount = idx.count / 3;
    for (let i = 0; i < triCount; i++) {
      const a = idx.getX(i * 3), b = idx.getX(i * 3 + 1), c = idx.getX(i * 3 + 2);
      const cy = (pos.getY(a) + pos.getY(b) + pos.getY(c)) / 3;
      if (cy < thresholdY) keep.push(a, b, c);
    }
  } else {
    const triCount = pos.count / 3;
    for (let i = 0; i < triCount; i++) {
      const a = i * 3, b = a + 1, c = a + 2;
      const cy = (pos.getY(a) + pos.getY(b) + pos.getY(c)) / 3;
      if (cy < thresholdY) keep.push(a, b, c);
    }
  }
  if (keep.length === 0) return null;
  const newGeom = geom.clone();
  newGeom.setIndex(keep);
  newGeom.computeBoundingBox();
  newGeom.computeBoundingSphere();
  return newGeom;
}

/**
 * Apply materials by mesh Y-position relative to the whole model.
 * Heuristics:
 *   table — top = thin slab in the top 15% of height; everything else = base
 *   chair — legs/frame = bottom 45%; seat & back = upper 55% (upholstery)
 */
function applyMaterials(group, kind, roleMat) {
  group.traverse(o => {
    if (!o.isMesh) return;
    const cy = o.userData.cy_norm ?? 0.5;
    const dy = o.userData.dy_norm ?? 0.5;
    let role;
    if (kind === 'table') {
      role = (cy > 0.83 && dy < 0.18) ? 'slab' : 'rod';
    } else /* chair */ {
      role = (cy < 0.45) ? 'rod' : 'slab';
    }
    o.material = roleMat[role] || roleMat.slab;
    o.castShadow = true;
    o.receiveShadow = true;
  });
}

// ═══════════════════════════════════════════════════════════════════════════
//  MATERIALS — texture cache + group dispatch
// ═══════════════════════════════════════════════════════════════════════════

const __textureCache = new Map();

function loadTex(url) {
  if (!url) return null;
  if (__textureCache.has(url)) return __textureCache.get(url);
  const tex = new THREE.TextureLoader().load(url);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.anisotropy = 16;
  __textureCache.set(url, tex);
  return tex;
}

function matFromGroupCode(group, code, fallbackHex = '#bbb', roughness = 0.5) {
  const url = photoTextureUrl(group, code);
  const map = loadTex(url);
  const metalness = group === 'metal' ? 0.55 : group === 'glass' ? 0.0 : 0.05;
  if (map) {
    return new THREE.MeshStandardMaterial({ map, roughness, metalness });
  }
  return new THREE.MeshStandardMaterial({
    color: new THREE.Color(fallbackHex), roughness, metalness,
  });
}

function matFromTop(item) {
  const top = item.topCode;
  let group, hex, rough = 0.45;
  if (['CLR', 'EXC', 'EXCB', 'EXCBR', 'MST'].includes(top)) { group = 'glass';      hex = '#dbe7e8'; rough = 0.05; }
  else if (top === 'CY01' || top === 'CY02')                { group = 'crystalart'; hex = '#c8c0b0'; rough = 0.35; }
  else if (['NC', 'RB', 'RN'].includes(top))                { group = 'wood';       hex = '#7a5a3a'; rough = 0.6; }
  else                                                       { group = 'ceramic';    hex = '#c0b8a8'; rough = 0.35; }
  const mat = matFromGroupCode(group, top, hex, rough);
  if (group === 'glass') { mat.transparent = true; mat.opacity = 0.62; mat.envMapIntensity = 1.0; }
  return mat;
}

function upholsteryDirForCode(code) {
  if (CHAIR_FABRICS.includes(code))                        return 'chair/fabric';
  if (CHAIR_MICRO_NUBUCK.some(x => x.code === code))       return 'chair/micro-nubuck';
  if (CHAIR_SYNTHETIC_LEATHER.some(x => x.code === code))  return 'chair/synthetic-leather';
  if (CHAIR_SOFT_LEATHER.some(x => x.code === code))       return 'chair/soft-leather';
  if (CHAIR_SOFT_LEATHER_GLOVE.some(x => x.code === code)) return 'chair/soft-leather-glove';
  return null;
}

function matFromUpholstery(code) {
  const dir = upholsteryDirForCode(code);
  const url = dir ? `/textures/materials/${dir}/${code}.jpg` : null;
  const map = loadTex(url);
  if (map) {
    // We can't tile the cached texture (that would mutate it for other
    // materials using the same swatch). The seat is small enough that one
    // full-image projection looks fine.
    return new THREE.MeshStandardMaterial({ map, roughness: 0.78, metalness: 0.02 });
  }
  return new THREE.MeshStandardMaterial({ color: 0xb5a78c, roughness: 0.85, metalness: 0.02 });
}

// ═══════════════════════════════════════════════════════════════════════════
//  SHARED SHAPE FACTORIES
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Stadium / racetrack shape — rectangle with semicircular short ends.
 * Used for `sag.*` variants. Width is along X, depth along Y in shape space.
 */
function shapeStadium(w, d) {
  const r = d / 2;
  const flat = Math.max(0, w - 2 * r);
  const s = new THREE.Shape();
  s.moveTo(-flat / 2, -r);
  s.lineTo(flat / 2, -r);
  s.absarc(flat / 2, 0, r, -Math.PI / 2, Math.PI / 2, false);
  s.lineTo(-flat / 2, r);
  s.absarc(-flat / 2, 0, r, Math.PI / 2, 3 * Math.PI / 2, false);
  return s;
}

/**
 * Rounded rectangle with parameterised corner radius. Used for biscuit shape
 * and as a chair-shell base.
 */
function shapeRoundedRect(w, d, radius) {
  const r = Math.min(radius, Math.min(w, d) / 2);
  const x = w / 2, y = d / 2;
  const s = new THREE.Shape();
  s.moveTo(-x + r, -y);
  s.lineTo(x - r, -y);
  s.absarc(x - r, -y + r, r, -Math.PI / 2, 0, false);
  s.lineTo(x, y - r);
  s.absarc(x - r, y - r, r, 0, Math.PI / 2, false);
  s.lineTo(-x + r, y);
  s.absarc(-x + r, y - r, r, Math.PI / 2, Math.PI, false);
  s.lineTo(-x, -y + r);
  s.absarc(-x + r, -y + r, r, Math.PI, 3 * Math.PI / 2, false);
  return s;
}

/**
 * Hexagon (polygon.300x130). Long sides parallel, short sides chamfered.
 * Approximation: extend the rectangle and pull the short ends inward.
 */
function shapeHexagon(w, d) {
  const cx = w / 2, cy = d / 2;
  const chamfer = d * 0.45;       // how far the short ends pull inward
  const s = new THREE.Shape();
  s.moveTo(-cx + chamfer, -cy);
  s.lineTo( cx - chamfer, -cy);
  s.lineTo( cx,             0);
  s.lineTo( cx - chamfer,  cy);
  s.lineTo(-cx + chamfer,  cy);
  s.lineTo(-cx,             0);
  s.closePath();
  return s;
}

/**
 * Ellipse / boat shape — used for Masterwood (WD M).
 * Approximated with a 64-segment polygon traced along the ellipse.
 */
function shapeEllipse(w, d) {
  const s = new THREE.Shape();
  const a = w / 2, b = d / 2;
  const N = 64;
  for (let i = 0; i <= N; i++) {
    const t = (i / N) * Math.PI * 2;
    const x = a * Math.cos(t);
    const y = b * Math.sin(t);
    if (i === 0) s.moveTo(x, y); else s.lineTo(x, y);
  }
  return s;
}

/**
 * Rectangle with one long edge made deliberately irregular — fake live-edge.
 * Seeded by w+d so the same size renders the same waviness every time.
 */
function shapeLiveEdge(w, d) {
  const s = new THREE.Shape();
  const x = w / 2, y = d / 2;
  // bottom edge: straight
  s.moveTo(-x, -y);
  s.lineTo( x, -y);
  // right short edge: straight
  s.lineTo( x,  y);
  // top edge: irregular — 16 small offsets
  const N = 16;
  let rng = (w * 13 + d * 7) & 0xffff;
  const rand = () => { rng = (rng * 9301 + 49297) & 0xffff; return rng / 0xffff; };
  for (let i = 1; i < N; i++) {
    const t = i / N;
    const px = x - t * (2 * x);
    const py = y - (rand() - 0.5) * d * 0.07;
    s.lineTo(px, py);
  }
  // left short edge
  s.lineTo(-x, y);
  s.closePath();
  return s;
}

// Extrude a Shape into a horizontal slab of given thickness, returns mesh.
function extrudeSlab(shape, thicknessMm, mat, bevelMm = 0) {
  const geom = new THREE.ExtrudeGeometry(shape, {
    depth: thicknessMm,
    bevelEnabled: bevelMm > 0,
    bevelThickness: bevelMm,
    bevelSize: bevelMm,
    bevelSegments: 3,
    curveSegments: 24,
  });
  // ExtrudeGeometry's default UVGenerator emits UVs in SHAPE-vertex coordinates
  // (which are millimetres at our scale). With RepeatWrapping that tiles the
  // texture ~3000× across a 3 m top → averages to a flat colour. Normalise UVs
  // to [0, 1] over the shape bbox so the texture renders as a single full image.
  geom.computeBoundingBox();
  const bb = geom.boundingBox;
  const uv = geom.attributes.uv;
  if (uv && bb) {
    const sx = Math.max(0.001, bb.max.x - bb.min.x);
    const sy = Math.max(0.001, bb.max.y - bb.min.y);
    for (let i = 0; i < uv.count; i++) {
      uv.setXY(
        i,
        (uv.getX(i) - bb.min.x) / sx,
        (uv.getY(i) - bb.min.y) / sy,
      );
    }
    uv.needsUpdate = true;
  }
  const mesh = new THREE.Mesh(geom, mat);
  // ExtrudeGeometry produces shape on XY plane, extruded in +Z.
  // We want it horizontal: rotate so the slab lies in XZ plane.
  mesh.rotation.x = -Math.PI / 2;
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

// ═══════════════════════════════════════════════════════════════════════════
//  X-BASE — single archetype, scales with table size
// ═══════════════════════════════════════════════════════════════════════════

export function buildBase(wMm, dMm, hMm, topThicknessMm, mat) {
  const g = new THREE.Group();
  const TOP_SPINE_H = 40;          // mm — central top crossbar thickness
  const strutH = hMm - topThicknessMm - TOP_SPINE_H;
  const PLATE_W = 90, PLATE_T = 14;

  // 4 angled flat struts from corner-inset floor positions to centre top.
  const inset = Math.min(wMm, dMm) * 0.16;
  const cornerXs = [ wMm / 2 - inset, -wMm / 2 + inset, -wMm / 2 + inset,  wMm / 2 - inset];
  const cornerZs = [ dMm / 2 - inset,  dMm / 2 - inset, -dMm / 2 + inset, -dMm / 2 + inset];
  for (let i = 0; i < 4; i++) {
    const cx = cornerXs[i], cz = cornerZs[i];
    const dx = -cx, dz = -cz;
    const horiz = Math.sqrt(dx * dx + dz * dz);
    const len = Math.sqrt(horiz * horiz + strutH * strutH);
    const strut = new THREE.Mesh(new THREE.BoxGeometry(PLATE_W, PLATE_T, len), mat);
    // Position at midpoint of the strut axis.
    strut.position.set(cx / 2, strutH / 2, cz / 2);
    // Orient long-axis along the diagonal: lookAt() makes -Z point to target,
    // we want +Z (the BoxGeometry's depth axis) along the diagonal toward centre top.
    strut.lookAt(0, strutH, 0);
    // Rotate so the wide face shows outward.
    strut.rotateZ(Math.atan2(cz, cx));
    strut.castShadow = true;
    g.add(strut);
  }

  // Top spine — two crossing horizontal plates at strut convergence.
  const spineXLen = Math.max(wMm * 0.35, 350);
  const spineZLen = Math.max(dMm * 0.35, 350);
  const spineX = new THREE.Mesh(new THREE.BoxGeometry(spineXLen, TOP_SPINE_H, PLATE_W * 0.8), mat);
  spineX.position.y = strutH + TOP_SPINE_H / 2;
  spineX.castShadow = true;
  g.add(spineX);
  const spineZ = new THREE.Mesh(new THREE.BoxGeometry(PLATE_W * 0.8, TOP_SPINE_H, spineZLen), mat);
  spineZ.position.y = strutH + TOP_SPINE_H / 2;
  spineZ.castShadow = true;
  g.add(spineZ);

  return g;
}

// ═══════════════════════════════════════════════════════════════════════════
//  TOP ARCHETYPES — dispatched by shape + variant
// ═══════════════════════════════════════════════════════════════════════════

function buildFlatRectTop(wMm, dMm, thickness, mat) {
  return extrudeSlab(shapeRoundedRect(wMm, dMm, 8), thickness, mat);
}

function buildSagTop(wMm, dMm, thickness, mat) {
  return extrudeSlab(shapeStadium(wMm, dMm), thickness, mat);
}

function buildBiscuitTop(wMm, dMm, thickness, mat) {
  // Biscuit = strongly-rounded rectangle (corner radius ~ d/3)
  return extrudeSlab(shapeRoundedRect(wMm, dMm, dMm * 0.42), thickness, mat);
}

function buildPolygonTop(wMm, dMm, thickness, mat) {
  return extrudeSlab(shapeHexagon(wMm, dMm), thickness, mat);
}

function buildRoundTop(wMm, thickness, mat) {
  const mesh = new THREE.Mesh(
    new THREE.CylinderGeometry(wMm / 2, wMm / 2, thickness, 96),
    mat,
  );
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

/**
 * Keramik Premium — thick ceramic slab with a thin brushed-metal profile band
 * peeking out from beneath the ceramic edge.
 *
 * Layout (in group-local Y, bottom-up):
 *   y = 0          ── profile bottom (slightly WIDER than ceramic on XZ)
 *   y = profileH   ── profile top  /  ceramic bottom
 *   y = thickness  ── ceramic top
 *
 * From above the user sees only the ceramic surface (full marble texture).
 * From the side the metal profile reads as a thin band at the very bottom.
 */
function buildKpTop(item, wMm, dMm, thickness, mat) {
  const g = new THREE.Group();
  const profileH       = 8;   // visible band height in mm
  const profileExtend  = 6;   // profile sticks out 6mm beyond ceramic on each side
  const profileCode    = item.insertOrProfile === 'BGR' ? 'BGR' : 'BBR';
  const profileMat     = matFromGroupCode('metal', profileCode, profileCode === 'BBR' ? '#a8835a' : '#787c80', 0.4);

  const profile = extrudeSlab(
    shapeRoundedRect(wMm + 2 * profileExtend, dMm + 2 * profileExtend, 14),
    profileH,
    profileMat,
  );
  // profile spans group-y [0, profileH]
  g.add(profile);

  const ceramic = extrudeSlab(
    shapeRoundedRect(wMm, dMm, 14),
    thickness - profileH,
    mat,
  );
  ceramic.position.y = profileH;   // ceramic sits ON TOP of the profile band
  g.add(ceramic);
  return g;
}

/**
 * Ker-Wood Round — wood outer ring around a recessed ceramic disc.
 */
function buildKwTop(item, wMm, thickness, mat) {
  const g = new THREE.Group();
  // Outer wood ring (use the table top wood code, defaulting to NC)
  const woodMat = matFromGroupCode('wood', item.topCode === 'RB' ? 'RB' : 'NC',
                                   item.topCode === 'RB' ? '#3a2a1c' : '#5a3a23', 0.6);
  const innerR = wMm * 0.36;
  const outerR = wMm / 2;
  // Ring via Shape with hole
  const ringShape = new THREE.Shape();
  for (let i = 0; i <= 64; i++) {
    const t = (i / 64) * Math.PI * 2;
    const x = outerR * Math.cos(t), y = outerR * Math.sin(t);
    if (i === 0) ringShape.moveTo(x, y); else ringShape.lineTo(x, y);
  }
  const hole = new THREE.Path();
  for (let i = 0; i <= 64; i++) {
    const t = (i / 64) * Math.PI * 2;
    const x = innerR * Math.cos(t), y = innerR * Math.sin(t);
    if (i === 0) hole.moveTo(x, y); else hole.lineTo(x, y);
  }
  ringShape.holes.push(hole);
  const ring = extrudeSlab(ringShape, thickness, woodMat);
  g.add(ring);
  // Inner ceramic disc — uses the ceramic insertOrProfile code
  const insertCode = item.insertOrProfile || 'KM02';
  const ceramicMat = matFromGroupCode('ceramic', insertCode, '#c0b8a8', 0.35);
  const disc = new THREE.Mesh(
    new THREE.CylinderGeometry(innerR - 2, innerR - 2, thickness - 1, 64),
    ceramicMat,
  );
  disc.position.y = thickness / 2;
  disc.castShadow = true;
  disc.receiveShadow = true;
  g.add(disc);
  return g;
}

function buildWoodSTop(wMm, dMm, thickness, mat) {
  // 4cm thick irregular live-edge plank — thicker than glass/ceramic
  return extrudeSlab(shapeLiveEdge(wMm, dMm), thickness, mat);
}

function buildWoodBTop(wMm, dMm, thickness, mat) {
  // Barrel = strongly-bevelled rectangle (visible chamfer on all sides)
  return extrudeSlab(shapeRoundedRect(wMm, dMm, 18), thickness, mat, 12);
}

function buildWoodMTop(wMm, dMm, thickness, mat) {
  // Masterwood = boat-shape (ellipse with bevelled edge)
  return extrudeSlab(shapeEllipse(wMm, dMm), thickness, mat, 10);
}

function buildWoodYTop(item, wMm, dMm, thickness) {
  // Bevelled dual-tone — outer rim in one wood, inner field in the other
  const g = new THREE.Group();
  const isNC = item.topCode === 'NC';
  const fieldCode = isNC ? 'NC' : 'RB';
  const rimCode   = isNC ? 'RB' : 'NC';
  const fieldMat = matFromGroupCode('wood', fieldCode, isNC ? '#5a3a23' : '#3a2a1c', 0.6);
  const rimMat   = matFromGroupCode('wood', rimCode,   isNC ? '#3a2a1c' : '#5a3a23', 0.6);
  // Outer rim slab (slightly thicker, slightly larger)
  const rim = extrudeSlab(shapeRoundedRect(wMm, dMm, 24), thickness, rimMat, 8);
  rim.position.y = -1;
  g.add(rim);
  // Inner field — inset by ~6cm
  const inset = 60;
  const field = extrudeSlab(shapeRoundedRect(wMm - 2 * inset, dMm - 2 * inset, 12), thickness - 2, fieldMat);
  g.add(field);
  return g;
}

/**
 * Master top dispatcher: returns a positioned Mesh/Group sitting on top of the
 * X-base (caller still positions Y).
 */
function buildTop(item, size) {
  const wMm = (size.diameter || size.w) * 10;
  const dMm = (size.diameter || size.d) * 10;
  const collection = item.collection;
  const variant = size.variant;

  // Round collections / round sub-variants
  if (size.shape === 'round') {
    if (collection === 'KW') return buildKwTop(item, wMm, 35, matFromTop(item));
    const thickness = collection === 'KRD' || collection === 'KPR' ? 35 : 30;
    return buildRoundTop(wMm, thickness, matFromTop(item));
  }

  // Keramik Premium — thick top with brushed profile
  if (collection === 'KP') {
    const thicknessCm = size.topThicknessCm || 5;
    return buildKpTop(item, wMm, dMm, thicknessCm * 10, matFromTop(item));
  }

  // Wood sub-variants
  if (variant === 'S') return buildWoodSTop(wMm, dMm, 40, matFromTop(item));
  if (variant === 'B') return buildWoodBTop(wMm, dMm, 50, matFromTop(item));
  if (variant === 'M') return buildWoodMTop(wMm, dMm, 50, matFromTop(item));
  if (variant === 'Y') return buildWoodYTop(item, wMm, dMm, 35);

  // Shape variants
  if (variant === 'sag')     return buildSagTop(wMm, dMm, 30, matFromTop(item));
  if (variant === 'biscuit') return buildBiscuitTop(wMm, dMm, 30, matFromTop(item));
  if (variant === 'polygon') return buildPolygonTop(wMm, dMm, 30, matFromTop(item));

  // Default — flat rectangle (CL/CA/KR non-shaped)
  return buildFlatRectTop(wMm, dMm, 30, matFromTop(item));
}

// ═══════════════════════════════════════════════════════════════════════════
//  TABLE — composites base + top
// ═══════════════════════════════════════════════════════════════════════════

// Returns the top-slab thickness in mm for a given (collection, size).
// Kept in one place so the base and the top stay in sync.
function topThicknessMm(item, size) {
  if (item.collection === 'KP') return (size.topThicknessCm || 5) * 10;
  if (size.variant === 'S')     return 40;
  if (size.variant === 'B')     return 50;
  if (size.variant === 'M')     return 50;
  if (size.variant === 'Y')     return 35;
  if (item.collection === 'KRD' || item.collection === 'KPR' || item.collection === 'KW') return 35;
  return 30;
}

// ─── Parametric fallback (used for round tables and when .3ds fails) ──────

function buildTableMeshParametric(item, size, wMm, dMm, hMm) {
  const g = new THREE.Group();
  const tThick = topThicknessMm(item, size);

  const baseMat = matFromGroupCode('metal', item.baseCode, BASE_BY_CODE[item.baseCode]?.hex || '#666', 0.4);
  g.add(buildBase(wMm, dMm, hMm, tThick, baseMat));

  const top = buildTop(item, size);
  if (size.shape === 'round' && item.collection !== 'KW') {
    top.position.y = hMm - tThick / 2;
  } else {
    top.position.y = hMm - tThick;
  }
  g.add(top);
  return g;
}

// ─── Real-model table (Cattelan Skorpio_240x120.3ds, rect tables only) ────

const TABLE_MODEL_URL = '/models/SKORPIO_240x120.3ds';

export function buildTableMesh(item) {
  const g = new THREE.Group();
  g.userData.itemId = item.id;
  g.userData.kind   = 'table';
  const sizes = SIZES_BY_COLLECTION[item.collection] || [];
  // Fallback to the first valid size if the item's sizeKey doesn't exist in
  // the chosen collection (e.g. AI guessed "200x100" for KR where the real
  // size is 200x106). Keeps the 3D preview alive — the data layer will still
  // surface the invalid key in the SKU until the user re-selects.
  const size = sizes.find(s => s.key === item.sizeKey) || sizes[0];
  if (!size) return g;

  const wMm = (size.diameter || size.w) * 10;
  const dMm = (size.diameter || size.d) * 10;
  const hMm = (size.h || 75) * 10;

  // Round tables can't be derived from a rect 3DS source — keep parametric.
  if (size.shape === 'round') {
    g.add(buildTableMeshParametric(item, size, wMm, dMm, hMm));
    return g;
  }

  const baseMat = matFromGroupCode('metal', item.baseCode, BASE_BY_CODE[item.baseCode]?.hex || '#666', 0.4);
  const topMat  = matFromTop(item);
  const tThick  = topThicknessMm(item, size);

  loadTDS(TABLE_MODEL_URL).then(({ obj, size: refSize }) => {
    // Deep-clone with independent geometries so we can filter without
    // mutating the cached source for other items.
    const clone = obj.clone(true);
    const geomVisited = new Set();
    clone.traverse(o => {
      if (o.isMesh && o.geometry && !geomVisited.has(o.geometry)) {
        o.geometry = o.geometry.clone();
        geomVisited.add(o.geometry);
      }
    });

    // Strip the top portion (triangles whose centroid Y ≥ refSize.y *
    // (1 − tThick/hMm)) from every mesh in the .3ds. After scaling, the
    // remaining geometry reaches exactly y = hMm − tThick, where our
    // parametric top begins — no overlap, no z-fight.
    const thresholdY = refSize.y * (1 - tThick / hMm);
    clone.traverse(o => {
      if (!o.isMesh || !o.geometry) return;
      const filtered = geometryRemoveTopRegion(o.geometry, thresholdY);
      if (filtered) o.geometry = filtered;
      o.material = baseMat;
      o.castShadow = true;
      o.receiveShadow = true;
    });

    // Non-uniform scale to match the requested size. The 240×120 reference
    // model stretches to other rect sizes — the X-base reads fine.
    clone.scale.set(wMm / refSize.x, hMm / refSize.y, dMm / refSize.z);
    g.add(clone);

    // Overlay parametric top with the correct material.
    const top = buildTop(item, size);
    top.position.y = hMm - tThick;
    g.add(top);
  }).catch(err => {
    console.warn('SKORPIO .3ds load failed, falling back to parametric:', err);
    g.add(buildTableMeshParametric(item, size, wMm, dMm, hMm));
  });

  return g;
}

// ═══════════════════════════════════════════════════════════════════════════
//  CHAIR — BELINDA / BELINDA ML
//
//  Shell (seat + curved back) extruded from a rounded-rect profile, plus
//  4 splayed legs and optional armrests (B variant).
// ═══════════════════════════════════════════════════════════════════════════

// ─── Real-model chair (BELINDA.3ds) — async, falls back to parametric ─────

const CHAIR_MODEL_URL = '/models/BELINDA.3ds';

/**
 * Parametric armrest pair, added on top of the loaded .3ds chair when the
 * "B" (with-arms) variant is selected. Cattelan ships one .3ds (the no-arms
 * shell), so we synthesise the armrests here.
 *
 * sizeMm = { w, d, h } of the chair in real mm coords (post-scale).
 */
/**
 * Upholstered "arm wings" — flat panels on each side of the seat, same
 * upholstery material as the shell. BELINDA's B-variant integrates armrests
 * into the upholstered shell (see Cattelan reference photo). We approximate
 * with a chunky rounded box on each side, attached to the seat side.
 */
function buildArmrests(sizeMm, upholMat) {
  const g = new THREE.Group();
  const { w, d, h } = sizeMm;
  const seatTopY = h * 0.50;
  const wingH    = h * 0.20;       // wing rises ≈20% of chair height above seat
  const wingW    = w * 0.13;       // wing thickness ≈13% of chair width
  const wingD    = d * 0.70;       // wing depth ≈70% of seat depth
  for (let s = -1; s <= 1; s += 2) {
    const wing = new THREE.Mesh(
      new THREE.BoxGeometry(wingW, wingH, wingD),
      upholMat,
    );
    // Wing flush with seat side, half-overlapping the shell, half outside.
    wing.position.set(s * (w / 2), seatTopY + wingH / 2, -d * 0.03);
    wing.castShadow = true;
    g.add(wing);
  }
  return g;
}

export function buildChairMesh(item) {
  const g = new THREE.Group();
  g.userData.itemId = item.id;
  g.userData.kind   = 'chair';
  const size = CHAIR_SIZES.find(s => s.key === item.sizeKey) || CHAIR_SIZES[0];
  const wMm = size.w * 10;
  const dMm = size.d * 10;
  const hMm = size.h * 10;

  const frameGroup = item.collection === 'BL' ? 'wood' : 'metal';
  const frameFallback = item.collection === 'BL' ? '#7a5a3a' : '#666';
  const frameMat = matFromGroupCode(frameGroup, item.frameCode, frameFallback, 0.55);
  const upholMat = matFromUpholstery(item.upholsteryCode);

  loadTDS(CHAIR_MODEL_URL).then(({ obj, size: refSize }) => {
    const clone = obj.clone(true);
    const s = hMm / refSize.y;
    clone.scale.set(s, s, s);
    applyMaterials(clone, 'chair', { rod: frameMat, slab: upholMat });
    g.add(clone);
    if (item.sizeKey === 'B') {
      // Use actual scaled extents (refSize.x * s, refSize.z * s) — uniform
      // scale `s` means the chair is `refSize.x * s` wide. Wings use
      // upholstery material to match the integrated-arm BELINDA design.
      g.add(buildArmrests({ w: refSize.x * s, d: refSize.z * s, h: hMm }, upholMat));
    }
  }).catch(err => {
    console.warn('BELINDA .3ds load failed, falling back to parametric:', err);
    g.add(buildChairMeshParametric(item));
  });

  return g;
}

function buildChairMeshParametric(item) {
  const g = new THREE.Group();
  const size = CHAIR_SIZES.find(s => s.key === item.sizeKey) || CHAIR_SIZES[0];
  const wMm = size.w * 10;     // 535mm
  const dMm = size.d * 10;     // 590mm
  const hMm = size.h * 10;     // 860mm
  const seatY = 460;

  const frameGroup = item.collection === 'BL' ? 'wood' : 'metal';
  const frameFallback = item.collection === 'BL' ? '#7a5a3a' : '#666';
  const frameMat = matFromGroupCode(frameGroup, item.frameCode, frameFallback, 0.55);

  const upholMat = matFromUpholstery(item.upholsteryCode);

  // ── Seat — rounded rectangle, ~80mm thick ────────────────────────────────
  const seatW = wMm * 0.92;
  const seatD = dMm * 0.80;
  const seatShape = shapeRoundedRect(seatW, seatD, 60);
  const seat = extrudeSlab(seatShape, 70, upholMat, 8);
  seat.position.y = seatY;
  g.add(seat);

  // ── Backrest — leaned back ~10° ─────────────────────────────────────────
  const backW = seatW * 0.96;
  const backH = hMm - seatY - 60;
  const backShape = shapeRoundedRect(backW, backH, 50);
  const back = new THREE.Mesh(
    new THREE.ExtrudeGeometry(backShape, {
      depth: 50,
      bevelEnabled: true, bevelThickness: 6, bevelSize: 6, bevelSegments: 3,
      curveSegments: 16,
    }),
    upholMat,
  );
  // Backrest is built in XY plane with +Z extrude. We want it standing up
  // facing forward (toward +Z), so rotate it -PI/2 around X to stand vertical,
  // then tip back ~10°.
  back.position.set(0, seatY + 70 + backH / 2, -seatD / 2 + 30);
  back.rotation.x = Math.PI * 0.06;   // ~11° tilt
  back.castShadow = true;
  back.receiveShadow = true;
  g.add(back);

  // ── 4 splayed legs (cylinders, slightly outward-angled) ─────────────────
  const legR = item.collection === 'BL' ? 18 : 14;
  const legLen = seatY;
  for (let i = 0; i < 4; i++) {
    const sx = (i % 2 === 0) ? 1 : -1;
    const sz = (i < 2) ? 1 : -1;
    const xTop = sx * (seatW / 2 - 40);
    const zTop = sz * (seatD / 2 - 40);
    const xBot = sx * (seatW / 2 + 25);
    const zBot = sz * (seatD / 2 + 25);
    const dx = xBot - xTop, dy = -legLen, dz = zBot - zTop;
    const len = Math.sqrt(dx * dx + dy * dy + dz * dz);
    const leg = new THREE.Mesh(new THREE.CylinderGeometry(legR, legR * 0.9, len, 16), frameMat);
    leg.position.set((xTop + xBot) / 2, (seatY + 0) / 2, (zTop + zBot) / 2);
    // Orient cylinder (default along Y) to point along the strut vector
    const v = new THREE.Vector3(dx, dy, dz).normalize();
    const up = new THREE.Vector3(0, 1, 0);
    const quat = new THREE.Quaternion().setFromUnitVectors(up, v.clone().negate());
    leg.quaternion.copy(quat);
    leg.castShadow = true;
    g.add(leg);
  }

  // ── Armrests for B variant ───────────────────────────────────────────────
  if (item.sizeKey === 'B') {
    for (let s = -1; s <= 1; s += 2) {
      // Arm support post
      const post = new THREE.Mesh(
        new THREE.CylinderGeometry(12, 12, 240, 12),
        frameMat,
      );
      post.position.set(s * (seatW / 2), seatY + 120, seatD * 0.15);
      post.castShadow = true;
      g.add(post);
      // Arm rail (horizontal)
      const arm = new THREE.Mesh(
        new THREE.BoxGeometry(35, 22, seatD * 0.55),
        frameMat,
      );
      arm.position.set(s * (seatW / 2), seatY + 240, -seatD * 0.08);
      arm.castShadow = true;
      g.add(arm);
    }
  }

  return g;
}
